/**
 * The three scoring axes on a match card, shown as 1-5 meters (PIVOT-091).
 *
 * The backend stores and sends 0-100 (architecture §5) and nothing here changes
 * that: `_fit`, the presentation bar and the refine diff all need the
 * resolution. The coarsening is presentation-only, because two digits advertise
 * a hundred gradations the pipeline cannot back — a language model's judgment is
 * not stable to a point, and the only thresholds anything acts on are 25 and 40.
 *
 * Two reader problems compound that, and are why five levels rather than a
 * rounded percentage: people reliably discriminate about five to seven levels of
 * a subjective quantity, and on a 0-100 scale a Belgian or French reader maps 50
 * onto a failing grade, so the best match in a day's corpus rendering as `42`
 * reads as a disaster instead of a partial fit.
 *
 * Not stars, deliberately: five literal stars recruit the product-review schema,
 * where 4.2 reads as bad because the ambient norm is 4.7.
 *
 * The three axes stay three. They are never averaged into one headline number —
 * the hand-written reference report is explicit about it ("ne fais PAS de moyenne
 * globale"), and a single match score is what makes a shortlist read as a dating
 * app.
 */

import { useI18n } from '../i18n'
import './ScoreBadges.css'

/**
 * How many levels a score is shown in.
 *
 * Exported because the meter's screen-reader label says "4 out of 5" and must
 * agree with the pips drawn. Reading the max off this instead of writing `5`
 * into the translations keeps the two from drifting.
 */
export const SCORE_BANDS = 5

/** One band's width on the stored 0-100 scale. The bands are equal, so this is
 *  the whole boundary table: 20 points each. */
const BAND_WIDTH = 100 / SCORE_BANDS

/**
 * The 0-100 score as a band from 1 to `SCORE_BANDS`.
 *
 * The boundaries, spelled out because that is what a reader wants from this
 * function: 0-19 is 1, 20-39 is 2, 40-59 is 3, 60-79 is 4, 80-100 is 5. The top
 * band is one point wider so that a perfect 100 lands in it rather than in a
 * sixth band of its own — the `Math.min` is what does that.
 *
 * A single expression rather than a lookup table, so there is one place the
 * boundaries come from. `ScoreBadges.test.tsx` asserts them at their edges.
 */
// eslint-disable-next-line react-refresh/only-export-components -- the mapping belongs beside the meter that draws it (PIVOT-091), and its test needs it
export function scoreBand(score: number): number {
  return Math.max(1, Math.min(SCORE_BANDS, Math.floor(score / BAND_WIDTH) + 1))
}

interface ScoreBadgesProps {
  expScore: number
  valuesScore: number
  /** `null` when the posting disclosed no pay — rendered as words, not a band. */
  salaryScore: number | null
}

/** One axis's meter: `band` pips filled of `SCORE_BANDS`.
 *
 * `role="img"` with a label, because the pips are `<span>`s that say nothing on
 * their own. A `meter` role would need a value AND a value-text to be read as
 * "4 out of 5" and is unevenly supported; one labelled image is read the same
 * way everywhere. */
function ScoreMeter({ band }: { band: number }) {
  const { t } = useI18n()
  return (
    <span
      className={`score-badge-meter score-band-${band}`}
      role="img"
      aria-label={t('a11y.scoreBand', { band, max: SCORE_BANDS })}
    >
      {Array.from({ length: SCORE_BANDS }, (_, i) => (
        <span
          key={i}
          className={i < band ? 'score-badge-pip score-badge-pip--on' : 'score-badge-pip'}
        />
      ))}
    </span>
  )
}

export function ScoreBadges({ expScore, valuesScore, salaryScore }: ScoreBadgesProps) {
  const { t } = useI18n()
  const axes: Array<{ key: string; label: string; value: number | null }> = [
    { key: 'exp', label: t('dashboard.scoreExperience'), value: expScore },
    { key: 'values', label: t('dashboard.scoreValues'), value: valuesScore },
    { key: 'salary', label: t('dashboard.scoreSalary'), value: salaryScore },
  ]

  return (
    <ul className="score-badges" aria-label={t('a11y.matchScores')}>
      {axes.map((axis) => (
        <li key={axis.key} className="score-badge">
          <span className="score-badge-label">{axis.label}</span>
          {axis.value === null ? (
            // Words, not pips. An unscored axis drawn as five empty pips would
            // read as a zero, and any band at all would be a verdict nobody
            // reached (constitution §VI).
            <span className="score-badge-undisclosed">{t('dashboard.scoreNotDisclosed')}</span>
          ) : (
            <ScoreMeter band={scoreBand(axis.value)} />
          )}
        </li>
      ))}
    </ul>
  )
}
